/**
 * A montagem da linha do tempo do atendimento, regra PURA: recebe o que as leituras devolveram e
 * devolve os eventos já com o texto do legado, na ordem da tela (`carregarTimelineCliente`,
 * `admin.html:5656-5741`; linha 221 do inventário).
 *
 * Sem banco e sem tela: quem lê é `ficha-timeline.ts` do pacote de dados, e quem desenha é a aba.
 * Assim a regra roda na suíte sem subir nada.
 *
 * A ORDEM DOS `add` É A DO LEGADO, e ela importa: a ordenação final é estável, então dois eventos
 * no mesmo instante saem na ordem em que foram somados.
 */

// A moeda idêntica à do legado.
import { formatarMoeda } from '../../../../comum/formatos.js';
// Os rótulos da jornada, do funil e das origens do lead.
import { JORNADA_ETAPA_LABEL } from '../../comum-ficha/catalogo.js';
import { ORIGENS_LEAD, STATUS_LEAD } from '../../../vendas/constantes.js';
// Os formatos das leituras.
import type {
  CompromissoDoHistorico,
  EventoDeJornada,
  MensagemDoHistorico,
  PagamentoDoHistorico,
  ParcelaPagaDoHistorico,
  SoCriadoEm,
  StatusDoHistorico,
} from '@studiolu/dados/areas/ficha-timeline';

/** Um evento da linha do tempo: o instante e a frase. */
export interface EventoDoHistorico {
  readonly data: Date;
  readonly texto: string;
}

/** O que a montagem recebe. Leitura que falhou chega vazia, como o `data || []` do legado. */
export interface EntradaDoHistorico {
  // O lead aberto: criação e origem. A origem só vem quando a ficha foi aberta pelo Kanban ou pelo
  // compromisso (`select('*')`); pela lista de clientes ela não vem (`admin.html:5878`).
  readonly lead: { readonly id: number; readonly data_criacao?: string | null; readonly origem?: string | null } | null;
  readonly historico: readonly EventoDeJornada[];
  readonly geral: SoCriadoEm | null;
  readonly avaliativo: SoCriadoEm | null;
  readonly atendimento: SoCriadoEm | null;
  readonly milhoes: SoCriadoEm | null;
  readonly mensagens: readonly MensagemDoHistorico[];
  readonly statusHistorico: readonly StatusDoHistorico[];
  readonly compromissos: readonly CompromissoDoHistorico[];
  readonly parcelas: readonly ParcelaPagaDoHistorico[];
  readonly pagamentos: readonly PagamentoDoHistorico[];
}

/** Monta os eventos, do mais recente para o mais antigo. */
export function montarEventos(entrada: EntradaDoHistorico): EventoDoHistorico[] {
  const eventos: EventoDoHistorico[] = [];
  // Instante vazio não vira evento, como o `if (data)` do `add` (`admin.html:5661`).
  const add = (data: string | null | undefined, texto: string) => {
    if (data) eventos.push({ data: new Date(data), texto });
  };

  // A criação do lead, com a origem quando ela veio (`5663-5666`).
  if (entrada.lead?.data_criacao) {
    const origem = entrada.lead.origem;
    const origemLabel = origem ? ORIGENS_LEAD[origem] || origem : null;
    add(entrada.lead.data_criacao, `Lead criada${origemLabel ? ' · origem: ' + origemLabel : ''}`);
  }

  // As transições da jornada; a primeira, sem etapa anterior, é o início (`5677-5683`).
  for (const h of entrada.historico) {
    const rotuloNova = JORNADA_ETAPA_LABEL[h.etapa_nova] || h.etapa_nova;
    const texto = h.etapa_anterior
      ? `Jornada: ${JORNADA_ETAPA_LABEL[h.etapa_anterior] || h.etapa_anterior} → ${rotuloNova}`
      : `Jornada iniciada em: ${rotuloNova}`;
    add(h.mudado_em, texto);
  }

  // As respostas e os checklists, pela data de criação da linha (`5685-5688`).
  if (entrada.geral?.criado_em) add(entrada.geral.criado_em, 'Respondeu o Questionário Geral');
  if (entrada.avaliativo?.criado_em) add(entrada.avaliativo.criado_em, 'Respondeu o Questionário Avaliativo');
  if (entrada.atendimento?.criado_em) add(entrada.atendimento.criado_em, 'Checklist de Atendimento iniciado');
  if (entrada.milhoes?.criado_em) add(entrada.milhoes.criado_em, 'Checklist de Milhões iniciado');

  // As mensagens, com prévia de 80 caracteres; sem texto, "(anexo)" (`5690-5694`).
  for (const m of entrada.mensagens) {
    const base = m.conteudo || '(anexo)';
    const previa = base.length > 80 ? base.slice(0, 80) + '…' : base;
    add(m.criado_em, `Mensagem ${m.remetente === 'consultora' ? 'da Lu' : 'da cliente'}: "${previa}"`);
  }

  // O que só existe com lead aberto (`5696-5727`), na ordem do legado: funil, compromissos,
  // parcelas pagas e contratos.
  if (entrada.lead?.id) {
    for (const h of entrada.statusHistorico) {
      const rotuloNovo = STATUS_LEAD[h.status_novo] || h.status_novo;
      const texto = h.status_anterior
        ? `Funil: ${STATUS_LEAD[h.status_anterior] || h.status_anterior} → ${rotuloNovo}`
        : `Lead entrou no funil em: ${rotuloNovo}`;
      add(h.mudado_em, texto);
    }
    for (const c of entrada.compromissos) {
      const dataCompromisso = new Date(c.data_hora_inicio).toLocaleDateString('pt-BR');
      add(c.criado_em, `Compromisso agendado: ${c.titulo || 'Sem título'} · ${dataCompromisso}`);
    }
    // DEFEITO DO LEGADO REPRODUZIDO (achado da A3, fora da seção 4): `data_pagamento` é uma data
    // sem hora, e `new Date('AAAA-MM-DD')` a lê como meia-noite em UTC. No fuso de Brasília o
    // evento aparece no dia ANTERIOR, às 21:00 (`admin.html:5720`). O resto do legado usa
    // `'T00:00:00'` para evitar isso (peça transversal 2); aqui ele não usa, e a migração repete.
    for (const p of entrada.parcelas) {
      add(p.data_pagamento, `Parcela ${p.numero} paga: ${formatarMoeda(p.valor)}`);
    }
    for (const p of entrada.pagamentos) {
      add(p.criado_em, `Contrato registrado: ${formatarMoeda(p.valor_total)}`);
    }
  }

  // Do mais recente para o mais antigo; o `sort` é estável, e empate mantém a ordem dos `add`.
  return eventos.sort((a, b) => b.data.getTime() - a.data.getTime());
}

/** "dd/mm/aaaa às HH:MM", a data de cada evento (`admin.html:5737`). */
export function dataDoEvento(data: Date): string {
  return `${data.toLocaleDateString('pt-BR')} às ${data.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
}
